<template>
<div>
    <section class="dashboard detail dealer" ref="container">
        <nav :class="section">
            <div></div>
            <div class="right"><div :class="['button', section]" @click="backToProduct()">Back to Product</div></div>
        </nav>
        <nav :class="['top', section]">
            <div>
                <h1>
                    <div>{{ productDetail.short_description }}</div>
                    <span class="breadcrumb" @click="backToProduct()"><font-awesome-icon icon="fa-times-circle" class="icon" />{{ dealerDetail.company_name }}  {{ dealerDetail.masked_address || dealerDetail.city + ', ' + dealerDetail.state }}</span>
                </h1>
            </div>
            <div class="meta">
                <div></div>
                <div>
                    <label>Start</label><div class="meta-data">{{ formatDate(totals.start_date) }}</div>
                </div>
                <div>
                    <label>End</label><div class="meta-data">{{ formatDate(totals.end_date) }}</div>
                </div>
            </div>
        </nav>

        <section v-if="productDetail.product_id" class="product-detail dealer">
            <div class="product-image">
                <img :src="productDetail.image_src" />
                <div class="meta">ID: {{ productDetail.product_id }}</div>
            </div>
            <div>
                <h3 class="underline">Product</h3>
                <label>UPC</label>{{ productDetail.upc }}
                <label>Manufacturer</label>{{ productDetail.manufacturer }}
                <label>Model</label>{{ productDetail.model }}
                <label>SKU</label>{{ productDetail.sku }}
                <label>Caliber</label><span v-if="productDetail.caliber">{{ productDetail.caliber }}</span><span v-else>N/A</span>
                <label>MAP</label><span v-if="productDetail.map">${{ productDetail.map }}</span><span v-else>N/A</span>
                <label>MSRP</label><span v-if="productDetail.msrp">${{ productDetail.msrp }}</span><span v-else>N/A</span>
            </div>
            <div>
                <h3 class="underline">Inventory</h3>
                <label>Qty On Hand</label>{{ formatNumber(productDetail.on_hand) }}
                <label>Cost On Hand</label>{{ formatCurrency(productDetail.cost_on_hand) }}
                <label>Retail On Hand</label>{{ formatCurrency(productDetail.price_on_hand) }}
                <label>Cost Each</label>
                <span>{{ formatCurrency(productDetail.cost_each) }}</span>
                <label>Price Each</label>
                <span>{{ formatCurrency(productDetail.price_each) }}</span>
                <label>Margin</label>
                <span v-if="productDetail.margin">{{ productDetail.margin }}%</span><span v-else>N/A</span>
                <label>GMROI</label>
                {{ productDetail.gmroi }}
            </div>
            <div>
                <h3 class="underline">Sales</h3>
                <label>Qty Sold</label>
                {{ productDetail.sold ? formatNumber(productDetail.sold) : '0' }}
                <label>Cost Sold</label>
                {{ productDetail.cost_sold ? formatCurrency(productDetail.cost_sold) : '$0.00' }}
                <label>Price Sold</label>
                {{ productDetail.price_sold ? formatCurrency(productDetail.price_sold) : '$0.00' }}
                <label>Profit Sold</label>
                {{ formatCurrency(productDetail.profit_sold)}}
                <label>Store Total Sales</label>
                {{ productDetail.total_price_sold ? formatCurrency(productDetail.total_price_sold) : '$0.00' }}
                <label>Product % of Total</label>
                {{ productDetail.percent_of_total_sales || '0' }}%
            </div>
            <div>
                <h3 class="underline">Suppliers</h3>
                <div v-for="(supplier, index) in productDetail.suppliers" :key="'supplier_' + index" style="margin: 0 0 1em 0;">
                    <div>{{ supplier.company_name }}</div>
                    <div class="two-columns">
                        <div><label>Qty Rcvd</label>{{ supplier.received }}</div>
                        <div><label>Rcvd Cost</label>{{ formatCurrency(supplier.cost_received) }}</div>
                    </div>
                </div>
            </div>
        </section>

        <section v-if="dealerDetail.location_id" class="product-detail-charts two-columns" style="grid-template-columns: 1fr 0.5fr 0.5fr;">
            <div class="product-detail-chart dealer">
                <h3>Inventory and sales monthly<font-awesome-icon icon="fa-info-circle" class="icon" @click="toggleLegend('inventory_sales_monthly')" /></h3>
                <div class="chart-wrapper">
                    <div ref="chart_inventory_sales_monthly" class="chart" style="height: 260px;"></div>
					<div v-if="!stats.inventory_sales_monthly || stats.inventory_sales_monthly.length == 0" class="no-data">No Data</div>
                    <div v-if="showLegend == 'inventory_sales_monthly'" class="product-detail-legend" @click="toggleLegend('inventory_sales_monthly')">{{ legends['inventory_sales_monthly'] }}</div>
                </div>
            </div>
            <div class="product-detail-chart product">
                <h3>Sales by type<font-awesome-icon icon="fa-info-circle" class="icon" @click="toggleLegend('sales_by_type')" /></h3>
                <div class="chart-wrapper">
                    <div ref="chart_sales_by_type" class="chart" style="height: 260px;"></div>
					<div v-if="!stats.sales_by_type || stats.sales_by_type.length == 0" class="no-data">No Data</div>
                    <div v-if="showLegend == 'sales_by_type'" class="product-detail-legend" @click="toggleLegend('sales_by_type')">{{ legends['sales_by_type'] }}</div>
                </div>
            </div>
            <div class="product-detail-chart dealer">
                <h3>Days On Hand<font-awesome-icon icon="fa-info-circle" class="icon" @click="toggleLegend('days_on_hand')" /></h3>
                <div class="chart-wrapper">
                    <div ref="chart_days_on_hand" class="chart" style="height: 260px;"></div>
					<div v-if="!stats.days_on_hand || stats.days_on_hand.length == 0" class="no-data">No Data</div>
                    <div v-if="showLegend == 'days_on_hand'" class="product-detail-legend" @click="toggleLegend('days_on_hand')">{{ legends['days_on_hand'] }}</div>
                </div>
            </div>
        </section>

        <div v-if="dealerDetail.location_id" style="margin: 1em 0;">
            <h3>Inventory Daily History</h3>
            <div v-if="loadingHistoryList" class="loading-list"><font-awesome-icon icon="fa-spinner" class="icon fa-spin" /></div>
            <table v-else class="list">
                <tr>
                    <th class="left">Date</th>
                    <th class="right">On Hand</th>
                    <th class="right">Cost On Hand</th>
                    <th class="right">Retail On Hand</th>
                    <th class="right">Received</th>
                    <th class="right">Sold</th>
                    <th class="right">Cost Sold</th>
                    <th class="right">Retail Sold</th>
                </tr>
                <tr v-for="(history, index) in historyList" :index="history.product_inventory_report_id">
                    <td class="dealer left">{{ formatDate(history.log_date) }}</td>
                    <td class="dealer right">{{ formatNumber(history.on_hand) }}</td>
                    <td class="dealer right">{{ formatCurrency(history.cost_on_hand) }}</td>
                    <td class="dealer right">{{ formatCurrency(history.price_on_hand) }}</td>
                    <td class="dealer right">{{ formatNumber(history.received) }}</td>
                    <td class="dealer right">{{ formatNumber(history.sold) }}</td>
                    <td class="dealer right">{{ formatCurrency(history.cost_sold) }}</td>
                    <td class="dealer right">{{ formatCurrency(history.price_sold) }}</td>
                </tr>
            </table>
            <nav class="bottom">
                <div></div>
                <pagination :paging="historyListPaging" :section="section" @updatePage="goToHistoryListPage" />
            </nav>
        </div>

        <nav class="bottom">
            <div></div>
            <div class="right"><div :class="['button', section]" @click="backToProduct()">Back to Product</div></div>
        </nav>

    </section>

    <message-modal v-if="message" section="dealer" :message="message" :loading="loadingStats" />

</div>
</template>

<script>
    export default {
    	name: 'dealer',
        props: ['section', 'location_id'],
        data() {
            return {
                loading: false,
                dealerDetail: {},
                productDetail: {},
                filters: {},
                totals: {},
                backToReportPath: '',
                message: '',
                loadingStats: false,
                stats: {},
                statsCompare: {},
                legends: {},
                showLegend: '',
                loadingHistoryList: false,
                historyList: [],
                historyListPaging: {},
                chartKey: '' // allows chart divs to reset when changing filters,
            }
        },
        created() {
            this.updateSource('dealer')
            if (sessionStorage.getItem('filters')) {
                this.filters = JSON.parse(sessionStorage.getItem('filters'))
                this.filters.location_id = this.location_id
                sessionStorage.setItem('filters', JSON.stringify(this.filters));
            }
            this.getDealer()
            this.getDealerProduct()
            this.getChartData()
            this.getDealerHistory()
            this.backToReportPath = '/product/' + this.filters.section + '/' + this.filters.product_id
        },
        mounted() {
        },
    	methods: {
            getDealer() {
                if (this.loadingDealer) { return }
                this.loadingDealer = true
                this.apiCall({ destination: 'get_dealer_detail', data: this.filters }).then(obj => {
                    if (obj.status == 'success') {
                        this.loadingDealer = false
                        this.dealerDetail = obj.detail
                        this.totals = obj.totals
                    }
                }).catch(err => {
                	console.log(err)
                })
            },
            getDealerProduct() {
                if (this.loadingDealerProduct) { return }
                this.loadingDealerProduct = true
                this.apiCall({ destination: 'get_dealer_product_detail', data: this.filters }).then(obj => {
                    if (obj.status == 'success') {
                        this.loadingDealerProduct = false
                        this.productDetail = obj.detail
                    }
                }).catch(err => {
                	console.log(err)
                })
            },
            getChartData() {
                if (this.loadingStats) { return }
                this.loadingStats = true
                this.apiCall({ destination: 'get_dealer_stats', data: this.filters }).then(obj => {
                    if (obj.status == 'success') {
                        this.loadingStats = false
                        this.stats = obj.stats
                        this.legends = obj.legends
                        this.createColumnLineChart(this.$refs.chart_inventory_sales_monthly, this.stats.inventory_sales_monthly, 'date', { value: 'price_on_hand', name: 'On Hand' }, { value: 'price_sold', name: 'Sold' }, 'month')
                        this.createPieChart(this.$refs.chart_sales_by_type, this.stats.sales_by_type, 'type', 'value', true, true, 65)
                        this.createPieChart(this.$refs.chart_days_on_hand, this.stats.days_on_hand, 'type', 'value', false, false, 65)
                    }
                }).catch(err => {
                	console.log(err)
                })
            },
            getDealerHistory() {
                if (this.loadingHistoryList) { return }
                this.loadingHistoryList = true
                if (!this.filters.dealer_history_list_page) {
                    this.filters.dealer_history_list_page = 1
                }
                this.apiCall({ destination: 'get_dealer_history', data: this.filters }).then(obj => {
                    if (obj.status == 'success') {
                        this.loadingHistoryList = false
                        this.historyList = obj.list
                        this.historyListPaging = obj.paging
                    }
                }).catch(err => {
                	console.log(err)
                })
            },
            toggleLegend(which) {
                if (this.showLegend == which) {
                    this.showLegend = ''
                } else {
                    this.showLegend = which
                }
            },
            goToHistoryListPage(page) {
                if (this.loadingHistoryList) { return }
                this.filters.dealer_history_list_page = page
                this.getDealerHistory()
            },
            backToProduct() {
                this.$emit('close')
            }
        }
    }
</script>
